/**
 * contractsApi.js · v2.7.35 (2026-02-21)
 * ═════════════════════════════════════════════════════════════════════
 * Thin wrapper around adminApi for the Enterprise Contracts module.
 * Centralizes endpoint paths + error shaping so individual pages stay
 * lean.
 */
import adminApi from "@/lib/adminApi";

const j = (p) => `/admin/contracts${p}`;

export const contractsApi = {
  // Meta
  listClassifications: () => adminApi.get(j("/meta/classifications")),

  // CRUD
  list: (params = {}) => adminApi.get(j(""), { params }),
  create: (payload) => adminApi.post(j(""), payload),
  get: (id) => adminApi.get(j(`/${id}`)),
  patch: (id, payload) => adminApi.patch(j(`/${id}`), payload),
  toggleActive: (id) => adminApi.post(j(`/${id}/toggle-active`)),
  remove: (id) => adminApi.delete(j(`/${id}`)),

  // Sub-accounts
  listSubs: (id) => adminApi.get(j(`/${id}/sub-accounts`)),
  createSub: (id, payload) => adminApi.post(j(`/${id}/sub-accounts`), payload),
  patchSub: (subId, payload) => adminApi.patch(j(`/sub-accounts/${subId}`), payload),
  deleteSub: (subId) => adminApi.delete(j(`/sub-accounts/${subId}`)),

  // Documents (primary · 200 MB)
  listDocs: (id) => adminApi.get(j(`/${id}/documents`)),
  uploadDoc: (id, file, label, onProgress) => {
    const form = new FormData();
    form.append("file", file);
    if (label) form.append("label", label);
    return adminApi.post(j(`/${id}/documents`), form, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: onProgress,
      timeout: 0,
    });
  },
  downloadDocUrl: (docId) => `/api/admin/contracts/documents/${docId}`,
  deleteDoc: (docId) => adminApi.delete(j(`/documents/${docId}`)),

  // Doc Center · v2.7.36 · 100 MB · extended RBAC (dispatch + supervisor write+download)
  listDocCenter: (id) => adminApi.get(j(`/${id}/doc-center`)),
  uploadDocCenter: (id, file, opts = {}, onProgress) => {
    const form = new FormData();
    form.append("file", file);
    if (opts.label) form.append("label", opts.label);
    if (opts.description) form.append("description", opts.description);
    if (opts.overwriteId) form.append("overwrite_id", opts.overwriteId);
    return adminApi.post(j(`/${id}/doc-center`), form, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: onProgress,
      timeout: 0,
    });
  },
  downloadDocCenter: (docId) => adminApi.get(`/admin/contracts/doc-center/${docId}`, { responseType: "blob" }),
  deleteDocCenter: (docId) => adminApi.delete(`/admin/contracts/doc-center/${docId}`),

  // Invoices (AR)
  listInvoices: (id, status) => adminApi.get(j(`/${id}/invoices`), { params: status ? { status } : {} }),
  createInvoice: (id, payload) => adminApi.post(j(`/${id}/invoices`), payload),
  patchInvoice: (invId, payload) => adminApi.patch(j(`/invoices/${invId}`), payload),
  markInvoicePaid: (invId) => adminApi.post(j(`/invoices/${invId}/mark-paid`)),

  // Checks
  listChecks: (params = {}) => adminApi.get(j("/checks"), { params }),
  createCheck: (payload) => adminApi.post(j("/checks"), payload),
  patchCheck: (checkId, payload) => adminApi.patch(j(`/checks/${checkId}`), payload),
  deleteCheck: (checkId) => adminApi.delete(j(`/checks/${checkId}`)),
};

export const CLASSIFICATION_LABELS = {
  personal: "Customer",
  business: "Business",
  municipal: "Municipal",
  state: "State",
  federal: "Federal",
  dot: "DOT",
};

export const CLASSIFICATION_KEYS = Object.keys(CLASSIFICATION_LABELS);

// URL-friendly slug ↔ canonical classification key.
// `customer` URL segment maps to `personal` API key (the spec says "personal,
// business, municipal, state, federal, or DOT" but the user-facing nav says
// "Customer Contracts").
export const URL_SLUG_TO_CLASSIFICATION = {
  customer: "personal",
  business: "business",
  municipal: "municipal",
  state: "state",
  federal: "federal",
  dot: "dot",
};

export const CLASSIFICATION_TO_URL_SLUG = Object.fromEntries(
  Object.entries(URL_SLUG_TO_CLASSIFICATION).map(([slug, key]) => [key, slug])
);
